import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import Plot from 'react-plotly.js';
import { Linkedin, Twitter, Github, Instagram, Facebook, Youtube, Music, Globe, Link as LinkIcon, Mail, Phone, Calendar } from "lucide-react";
import Button from "./ui/Button";
import {
  fetchUserByEmail,
  syncUser,
  fetchCards,
  fetchCardLinks,
  fetchVisitStats,
  fetchVisitTimeline,
  fetchLinkClickTimeline,
  fetchLinkClicks,
  fetchCardAnalytics,
  User,
  UserCreate,
  Card,
  CardLink,
  CardAnalytics,
  VisitStats,
} from "../utils/api";

const SocialIcon: React.FC<{ name: string; className?: string }> = ({ name, className = "w-5 h-5" }) => {
  const icons: Record<string, React.ReactNode> = {
    linkedin: <Linkedin className={className} />,
    twitter: <Twitter className={className} />,
    github: <Github className={className} />,
    instagram: <Instagram className={className} />,
    facebook: <Facebook className={className} />,
    youtube: <Youtube className={className} />,
    tiktok: <Music className={className} />,
    globe: <Globe className={className} />,
    link: <LinkIcon className={className} />,
    mail: <Mail className={className} />,
    phone: <Phone className={className} />,
    calendar: <Calendar className={className} />,
  };
  return <>{icons[name] || <LinkIcon className={className} />}</>;
};

// Fill in missing dates so charts show the full time range
function fillDateRange(
  data: Array<{ date: string; views: number }>,
  period: string
): Array<{ date: string; views: number }> {
  const now = new Date();
  let start: Date;

  switch (period) {
    case '7d':
      start = new Date(now);
      start.setDate(start.getDate() - 6);
      break;
    case '3m':
      start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      break;
    case '6m':
      start = new Date(now);
      start.setMonth(start.getMonth() - 6);
      break;
    case '1y':
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start = new Date(now);
      start.setDate(start.getDate() - 6);
  }

  const dataMap = new Map<string, number>();
  data.forEach(d => {
    const key = String(d.date).split('T')[0];
    dataMap.set(key, d.views);
  });

  const result: Array<{ date: string; views: number }> = [];

  if (period === '1y') {
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    while (cursor <= end) {
      const key = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
      result.push({ date: key, views: dataMap.get(key) || 0 });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  } else {
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(0, 0, 0, 0);
    while (cursor <= end) {
      const key = cursor.toISOString().split('T')[0];
      result.push({ date: key, views: dataMap.get(key) || 0 });
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return result;
}

// Format the rolling-7-day eyebrow date range, e.g. "May 1 – May 7, 2026"
function weekRangeLabel(): string {
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return `${fmt(start)} – ${fmt(end)}, ${end.getFullYear()}`;
}

// Build an SVG path (line + closed area) from numeric points fitted into a viewBox
function svgPath(points: number[], width: number, height: number, padY = 4) {
  if (points.length === 0) return { line: '', area: '' };
  const max = Math.max(...points, 1);
  const min = 0;
  const range = max - min || 1;
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;
  const coords = points.map((v, i) => {
    const x = i * stepX;
    const y = height - padY - ((v - min) / range) * (height - padY * 2);
    return [x, y] as const;
  });
  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const area = `${line} L ${width} ${height} L 0 ${height} Z`;
  return { line, area, coords };
}

// Tiny inline sparkline used inside link-row trend cells
const MicroSparkline: React.FC<{ points: number[] }> = ({ points }) => {
  const { line } = svgPath(points, 60, 16, 2);
  if (!line) return null;
  return (
    <svg viewBox="0 0 60 16" className="w-14 h-4 inline-block" aria-hidden="true">
      <path d={line} fill="none" stroke="#1f1410" strokeWidth="1.25" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const Insights: React.FC = () => {
  const { user } = useUser();
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const card = cards[selectedCardIndex] || null;
  const [links, setLinks] = useState<CardLink[]>([]);
  const [visitStats, setVisitStats] = useState<VisitStats | null>(null);
  const [linkClicks, setLinkClicks] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const isPaidUser = dbUser?.subscription === 'pro' || dbUser?.subscription === 'paid';
  const [analytics, setAnalytics] = useState<CardAnalytics | null>(null);
  const [statsSubTab, setStatsSubTab] = useState<'overview' | 'links' | 'audience'>('overview');
  const [timeFrame, setTimeFrame] = useState<'7d' | '3m' | '6m' | '1y'>('7d');
  const [timelineData, setTimelineData] = useState<Array<{ date: string; views: number }>>([]);
  const [linkTimelineData, setLinkTimelineData] = useState<Array<{ linkId: string; linkTitle: string; date: string; clicks: number }>>([]);

  useEffect(() => {
    document.title = "Insights - WhoBrew";
  }, []);

  const loadData = useCallback(async () => {
    if (!user) return;
    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;

    setLoading(true);
    try {
      let fetchedUser: User;
      try {
        fetchedUser = await fetchUserByEmail(email);
      } catch {
        const userData: UserCreate = { username: email.split("@")[0], email, firstName: user.firstName || "", lastName: user.lastName || "" };
        fetchedUser = await syncUser(userData);
      }
      setDbUser(fetchedUser);

      const userCards = await fetchCards(fetchedUser.id).catch(() => [] as Card[]);
      setCards(userCards);

      if (userCards.length > 0) {
        const firstCard = userCards[0];
        const [cardLinks, stats, clicks] = await Promise.all([
          fetchCardLinks(firstCard.id).catch(() => [] as CardLink[]),
          fetchVisitStats(firstCard.id),
          fetchLinkClicks(firstCard.id),
        ]);
        setLinks(cardLinks.sort((a, b) => a.order - b.order));
        setVisitStats(stats);
        setLinkClicks(clicks);
      }
    } catch { /* swallow */ } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) loadData(); }, [user, loadData]);

  // Reload when selected card changes
  useEffect(() => {
    if (!card) return;
    Promise.all([
      fetchCardLinks(card.id).catch(() => [] as CardLink[]),
      fetchVisitStats(card.id),
      fetchLinkClicks(card.id),
      fetchCardAnalytics(card.id).catch(() => null),
    ]).then(([cardLinks, stats, clicks, analyticsData]) => {
      setLinks(cardLinks.sort((a, b) => a.order - b.order));
      setVisitStats(stats);
      setLinkClicks(clicks);
      setAnalytics(analyticsData);
    });
  }, [card?.id]);

  // Fetch timeline data when timeFrame changes
  useEffect(() => {
    if (!card || !isPaidUser) return;
    fetchVisitTimeline(card.id, timeFrame).then(raw => {
      setTimelineData(fillDateRange(raw, timeFrame));
    });
    fetchLinkClickTimeline(card.id, timeFrame).then(setLinkTimelineData);
  }, [card?.id, timeFrame, isPaidUser]);

  // Derived: WoW trend (last 7 vs previous 7) — only when timeline is daily and ≥14 entries
  const weekTrend = useMemo(() => {
    if (timeFrame === '1y') return null;
    if (timelineData.length < 14) return null;
    const last7 = timelineData.slice(-7).reduce((s, d) => s + d.views, 0);
    const prev7 = timelineData.slice(-14, -7).reduce((s, d) => s + d.views, 0);
    if (prev7 === 0) return null;
    return Math.round(((last7 - prev7) / prev7) * 100);
  }, [timelineData, timeFrame]);

  // Derived: hero sparkline points (last 7 daily entries)
  const heroSparkline = useMemo(() => {
    if (timeFrame === '1y' || timelineData.length === 0) return [];
    return timelineData.slice(-7).map(d => d.views);
  }, [timelineData, timeFrame]);

  // Derived: total link clicks
  const totalLinkClicks = useMemo(
    () => Object.values(linkClicks).reduce((s, c) => s + c, 0),
    [linkClicks]
  );

  // Derived: per-link sparkline series from linkTimelineData
  const perLinkSparklines = useMemo(() => {
    const map: Record<string, number[]> = {};
    linkTimelineData.forEach(row => {
      if (!map[row.linkId]) map[row.linkId] = [];
      map[row.linkId].push(row.clicks);
    });
    return map;
  }, [linkTimelineData]);

  // CSV Export
  const handleExportCSV = () => {
    if (!card || links.length === 0) return;
    const now = new Date().toLocaleDateString();
    let csv = `WhoBrew Link Performance Report\r\nCard: ${card.name}\r\nGenerated: ${now}\r\n\r\n`;
    csv += `SUMMARY\r\nTotal Card Views,${visitStats?.totalVisits || 0}\r\nViews Today,${visitStats?.todayVisits || 0}\r\nViews This Week,${visitStats?.weekVisits || 0}\r\nViews This Month,${visitStats?.monthVisits || 0}\r\n\r\n`;
    csv += 'LINK PERFORMANCE\r\nLink Title,URL,Category,Clicks\r\n';
    links.forEach(link => {
      csv += `"${(link.title || '').replace(/"/g, '""')}","${(link.url || '').replace(/"/g, '""')}","${((link as any).category || 'Other').replace(/"/g, '""')}",${linkClicks[link.id] || 0}\r\n`;
    });
    const totalClicks = links.reduce((sum, l) => sum + (linkClicks[l.id] || 0), 0);
    csv += `\r\nTotal Clicks,,, ${totalClicks}\r\n`;
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whobrew-${card.username}-stats-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50">
        <div className="border-b border-latte-300 dark:border-latte-800">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-14 pb-12">
            <div className="animate-pulse space-y-3">
              <div className="h-3 bg-cream-100 dark:bg-coffee-800 rounded w-24" />
              <div className="h-12 bg-cream-100 dark:bg-coffee-800 rounded w-80" />
              <div className="h-4 bg-cream-100 dark:bg-coffee-800 rounded w-56" />
            </div>
          </div>
        </div>
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-10 pb-24">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-cream-100 dark:bg-coffee-800 rounded-lg" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-latte-300 dark:border-latte-800 rounded-lg overflow-hidden">
              {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-cream-100 dark:bg-coffee-800" />)}
            </div>
            <div className="h-72 bg-cream-100 dark:bg-coffee-800 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50">
        <div className="border-b border-latte-300 dark:border-latte-800">
          <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-14 pb-12">
            <div className="eyebrow mb-5">Insights · Your card</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-display-md text-coffee-900 dark:text-cream-50 tracking-tight">
              Your card analytics<span className="text-accent-500">.</span>
            </h1>
          </div>
        </div>
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-24">
          <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-12 text-center">
            <div className="w-12 h-12 rounded bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 flex items-center justify-center mx-auto mb-5">
              <LinkIcon className="w-5 h-5 text-coffee-400 dark:text-cream-400" />
            </div>
            <div className="eyebrow mb-3">No data yet</div>
            <h3 className="font-display text-2xl text-coffee-900 dark:text-cream-50 mb-2">No card to track.</h3>
            <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-xs mx-auto mb-5">Create your virtual card to start tracking views, clicks, and audience.</p>
            <Button to="/my-card" variant="primary">Create my card</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-50 dark:bg-coffee-900 text-coffee-900 dark:text-cream-50">

      {/* ───────── Page header ───────── */}
      <div className="border-b border-latte-300 dark:border-latte-800">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-14 pb-12">
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <div className="max-w-2xl">
              <p className="eyebrow mb-5">Insights · Your card</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-display-md text-coffee-900 dark:text-cream-50 tracking-tight leading-[1.04]">
                Your card analytics<span className="text-accent-500">.</span>
              </h1>
              <p className="mt-4 text-coffee-600 dark:text-cream-200 max-w-lg">
                Visits, clicks, and audience for{' '}
                <span className="text-coffee-900 dark:text-cream-50 font-medium">{card.name || `Card ${selectedCardIndex + 1}`}</span>.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              {cards.length > 1 && (
                <div className="relative">
                  <select
                    value={selectedCardIndex}
                    onChange={(e) => setSelectedCardIndex(parseInt(e.target.value))}
                    className="appearance-none h-10 pl-3 pr-9 border border-latte-300 dark:border-latte-800 rounded bg-cream-50 dark:bg-coffee-900 text-sm text-coffee-900 dark:text-cream-50 hover:bg-cream-100 focus:outline-none focus:ring-2 focus:ring-coffee-700 focus:ring-offset-2 focus:ring-offset-cream-50"
                  >
                    {cards.map((c, i) => (
                      <option key={c.id} value={i}>{c.name || `Card ${i + 1}`}</option>
                    ))}
                  </select>
                  <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b4a36" strokeWidth="2" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              {isPaidUser && links.length > 0 && (
                <Button variant="secondary" size="sm" onClick={handleExportCSV}>Export CSV</Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ───────── Sub-tabs (underline) ───────── */}
      <nav aria-label="Insights sections" className="border-b border-latte-300 dark:border-latte-800 bg-cream-50 dark:bg-coffee-900 sticky top-0 z-10">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="flex items-end gap-8 text-sm">
            {(['overview', 'audience', 'links'] as const).map(tab => {
              const active = statsSubTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setStatsSubTab(tab)}
                  className={`relative py-3 font-medium capitalize transition-colors ${
                    active ? 'text-coffee-900' : 'text-coffee-600 hover:text-coffee-900'
                  }`}
                >
                  {tab}
                  {active && <span aria-hidden="true" className="absolute left-0 right-0 -bottom-px h-px bg-coffee-900" />}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* ───────── Content ───────── */}
      <main className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-10 pb-24 space-y-10">

        {/* ── Overview tab ── */}
        {statsSubTab === 'overview' && visitStats && (
          <>
            {/* Hero stat */}
            <section className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-8 sm:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
                <div className={heroSparkline.length === 7 ? 'lg:col-span-7' : 'lg:col-span-12'}>
                  <p className="eyebrow mb-6">This week · {weekRangeLabel()}</p>
                  <p className="font-display text-[88px] sm:text-[120px] lg:text-[160px] leading-[0.85] text-coffee-900 dark:text-cream-50 tracking-tight tabular-nums">
                    {visitStats.weekVisits.toLocaleString()}<span className="text-accent-500">.</span>
                  </p>
                  <p className="font-display text-2xl sm:text-3xl leading-tight text-coffee-900 dark:text-cream-50 mt-6 max-w-xl">
                    card views in the last 7 days.
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-coffee-600 dark:text-cream-200">
                    {weekTrend !== null && (
                      <>
                        <span className={`inline-flex items-center gap-1.5 font-medium ${weekTrend >= 0 ? 'text-success-700' : 'text-danger-500'}`}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            {weekTrend >= 0
                              ? <path d="M7 17L17 7M17 7H9M17 7v8" strokeLinecap="round" strokeLinejoin="round" />
                              : <path d="M17 7L7 17M7 17h8M7 17V9" strokeLinecap="round" strokeLinejoin="round" />}
                          </svg>
                          <span className="font-mono">{weekTrend >= 0 ? '+' : ''}{weekTrend}%</span>
                        </span>
                        <span>vs. previous 7 days</span>
                        <span className="text-latte-300" aria-hidden="true">·</span>
                      </>
                    )}
                    <span>
                      <span className="font-mono text-coffee-900 dark:text-cream-50">{(analytics?.uniqueVisitors ?? 0).toLocaleString()}</span> unique visitors
                    </span>
                    <span className="text-latte-300" aria-hidden="true">·</span>
                    <span>
                      <span className="font-mono text-coffee-900 dark:text-cream-50">{visitStats.todayVisits.toLocaleString()}</span> today
                    </span>
                  </div>
                </div>

                {heroSparkline.length === 7 && (
                  <div className="lg:col-span-5">
                    <p className="eyebrow mb-3">Daily views</p>
                    <HeroSparkline points={heroSparkline} />
                    <div className="mt-2 grid grid-cols-7 text-center font-mono text-[10px] text-coffee-400 dark:text-cream-400">
                      {Array.from({ length: 7 }).map((_, i) => {
                        const d = new Date();
                        d.setDate(d.getDate() - (6 - i));
                        return <span key={i}>{d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase()}</span>;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Stat row */}
            <section aria-label="Key metrics">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-latte-300 dark:border-latte-800 rounded-lg divide-y sm:divide-y-0 sm:divide-x divide-latte-300 bg-cream-50 dark:bg-coffee-900 overflow-hidden">
                <article className="p-6">
                  <p className="eyebrow text-coffee-600 dark:text-cream-200">Total views</p>
                  <p className="font-display text-4xl sm:text-[40px] leading-none text-coffee-900 dark:text-cream-50 mt-3 tabular-nums">
                    {visitStats.totalVisits.toLocaleString()}
                  </p>
                  <p className="mt-4 text-xs text-coffee-600 dark:text-cream-200">All-time</p>
                </article>
                <article className="p-6">
                  <p className="eyebrow text-coffee-600 dark:text-cream-200">Unique visitors</p>
                  <p className="font-display text-4xl sm:text-[40px] leading-none text-coffee-900 dark:text-cream-50 mt-3 tabular-nums">
                    {(analytics?.uniqueVisitors ?? 0).toLocaleString()}
                  </p>
                  <p className="mt-4 text-xs text-coffee-600 dark:text-cream-200">All-time</p>
                </article>
                <article className="p-6">
                  <p className="eyebrow text-coffee-600 dark:text-cream-200">Link clicks</p>
                  <p className="font-display text-4xl sm:text-[40px] leading-none text-coffee-900 dark:text-cream-50 mt-3 tabular-nums">
                    {totalLinkClicks.toLocaleString()}
                  </p>
                  <p className="mt-4 text-xs text-coffee-600 dark:text-cream-200">
                    {visitStats.totalVisits > 0 ? (
                      <>
                        <span className="font-mono text-coffee-900 dark:text-cream-50">
                          {((totalLinkClicks / visitStats.totalVisits) * 100).toFixed(1)}%
                        </span>{' '}click rate
                      </>
                    ) : 'No views yet'}
                  </p>
                </article>
                <article className="p-6">
                  <p className="eyebrow text-coffee-600 dark:text-cream-200">Today</p>
                  <p className="font-display text-4xl sm:text-[40px] leading-none text-coffee-900 dark:text-cream-50 mt-3 tabular-nums">
                    {visitStats.todayVisits.toLocaleString()}
                  </p>
                  <p className="mt-4 text-xs text-coffee-600 dark:text-cream-200">Views in the last 24h</p>
                </article>
              </div>
            </section>

            {/* Timeline chart — pro only */}
            {isPaidUser ? (
              <section className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-6 sm:p-8">
                <header className="flex items-end justify-between gap-4 flex-wrap mb-8">
                  <div>
                    <p className="eyebrow mb-2">Activity</p>
                    <h2 className="font-display text-2xl sm:text-3xl leading-tight text-coffee-900 dark:text-cream-50 tracking-tight">
                      Card views over time<span className="text-accent-500">.</span>
                    </h2>
                  </div>
                  <div role="tablist" aria-label="Time range" className="inline-flex gap-1 bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 rounded p-1 text-sm">
                    {([
                      { id: '7d' as const, label: '7d' },
                      { id: '3m' as const, label: '3m' },
                      { id: '6m' as const, label: '6m' },
                      { id: '1y' as const, label: '1y' },
                    ]).map(tf => (
                      <button
                        key={tf.id}
                        role="tab"
                        aria-selected={timeFrame === tf.id}
                        onClick={() => setTimeFrame(tf.id)}
                        className={`px-3 py-1.5 rounded-sm transition-colors ${
                          timeFrame === tf.id ? 'bg-coffee-900 text-cream-50 font-medium' : 'text-coffee-600 hover:text-coffee-900'
                        }`}
                      >
                        {tf.label}
                      </button>
                    ))}
                  </div>
                </header>

                {timelineData.length > 0 ? (
                  <Plot
                    data={[{
                      x: timelineData.map(d => String(d.date).split('T')[0]),
                      y: timelineData.map(d => d.views),
                      type: 'scatter',
                      mode: 'lines+markers',
                      fill: 'tozeroy',
                      line: { color: '#1f1410', width: 1.5, shape: 'linear' },
                      marker: { color: '#1f1410', size: 3 },
                      fillcolor: 'rgba(184, 82, 26, 0.08)',
                      hovertemplate: '<b>%{y}</b> views<br>%{x}<extra></extra>',
                    }]}
                    layout={{
                      height: 280,
                      margin: { l: 40, r: 16, t: 8, b: 36 },
                      xaxis: {
                        tickformat: timeFrame === '1y' ? '%b %Y' : '%b %d',
                        gridcolor: '#dcc6a3',
                        griddash: 'dot',
                        tickfont: { size: 10, color: '#a8967a', family: 'IBM Plex Mono, monospace' },
                        showline: false,
                        zeroline: false,
                      },
                      yaxis: {
                        gridcolor: '#dcc6a3',
                        griddash: 'dot',
                        tickfont: { size: 10, color: '#a8967a', family: 'IBM Plex Mono, monospace' },
                        zeroline: false,
                        rangemode: 'tozero',
                      },
                      paper_bgcolor: 'transparent',
                      plot_bgcolor: 'transparent',
                      font: { family: 'Inter, sans-serif' },
                      hoverlabel: { bgcolor: '#1f1410', font: { color: '#fbf7f1', family: 'Inter, sans-serif' } },
                    }}
                    config={{ displayModeBar: false, responsive: true }}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <p className="text-sm text-coffee-500 dark:text-cream-300 text-center py-8">No view data for this period yet.</p>
                )}

                {/* Annotation strip */}
                {timelineData.length > 0 && (() => {
                  const peak = timelineData.reduce((m, d) => d.views > m.views ? d : m, timelineData[0]);
                  const total = timelineData.reduce((s, d) => s + d.views, 0);
                  const avg = total / timelineData.length;
                  return (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-latte-300 dark:border-latte-800">
                      <div>
                        <p className="eyebrow mb-1">Peak</p>
                        <p className="text-coffee-900 dark:text-cream-50 font-medium text-sm">
                          {peak.date} · <span className="font-mono">{peak.views} {peak.views === 1 ? 'view' : 'views'}</span>
                        </p>
                      </div>
                      <div>
                        <p className="eyebrow mb-1">Avg / period</p>
                        <p className="text-coffee-900 dark:text-cream-50 font-medium text-sm">
                          <span className="font-mono">{avg.toFixed(1)} {avg === 1 ? 'view' : 'views'}</span>
                        </p>
                      </div>
                      <div>
                        <p className="eyebrow mb-1">Total</p>
                        <p className="text-coffee-900 dark:text-cream-50 font-medium text-sm">
                          <span className="font-mono">{total.toLocaleString()}</span> across {timelineData.length} {timeFrame === '1y' ? 'months' : 'days'}
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </section>
            ) : (
              <ProUpsell
                title="Timeline charts"
                body="See how your card performs over time with daily, weekly, and monthly trends."
              />
            )}

            {/* Footnotes (Overview only) */}
            <Footnotes />
          </>
        )}

        {/* ── Audience tab ── */}
        {statsSubTab === 'audience' && (
          <div className="space-y-8">
            {!analytics || (analytics.devices.length === 0 && analytics.countries.length === 0) ? (
              <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-12 text-center">
                <div className="w-12 h-12 rounded bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 flex items-center justify-center mx-auto mb-5">
                  <Globe className="w-5 h-5 text-coffee-400 dark:text-cream-400" />
                </div>
                <div className="eyebrow mb-3">No data yet</div>
                <h3 className="font-display text-2xl text-coffee-900 dark:text-cream-50 mb-2">No audience data yet.</h3>
                <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-xs mx-auto">Analytics populate as people view your card.</p>
              </div>
            ) : (
              <>
                {/* Sources + Devices */}
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                  {analytics.referrers.length > 0 && (
                    <div className="lg:col-span-7 bg-cream-100 dark:bg-coffee-800 rounded-lg p-6 sm:p-8">
                      <header className="flex items-end justify-between gap-3 mb-6">
                        <div>
                          <p className="eyebrow mb-1.5">Where viewers came from</p>
                          <h2 className="font-display text-xl sm:text-2xl leading-tight text-coffee-900 dark:text-cream-50 tracking-tight">
                            Traffic sources<span className="text-accent-500">.</span>
                          </h2>
                        </div>
                      </header>
                      <ol className="space-y-5">
                        {(() => {
                          const max = analytics.referrers[0]?.count || 1;
                          const total = analytics.referrers.reduce((s, r) => s + r.count, 0) || 1;
                          return analytics.referrers.map((r, i) => (
                            <li key={i}>
                              <div className="flex items-baseline justify-between mb-1.5 text-sm">
                                <span className="text-coffee-900 dark:text-cream-50 font-medium">{r.label}</span>
                                <span className="font-mono text-coffee-700 dark:text-cream-200">
                                  {r.count.toLocaleString()} <span className="text-coffee-400 dark:text-cream-400">· {((r.count / total) * 100).toFixed(1)}%</span>
                                </span>
                              </div>
                              <div className="h-1.5 bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-coffee-900 transition-[width] duration-700 ease-out motion-reduce:transition-none"
                                  style={{ width: `${(r.count / max) * 100}%` }}
                                />
                              </div>
                            </li>
                          ));
                        })()}
                      </ol>
                    </div>
                  )}

                  <div className={`${analytics.referrers.length > 0 ? 'lg:col-span-5' : 'lg:col-span-12'} space-y-6`}>
                    {analytics.devices.length > 0 && (() => {
                      const total = analytics.devices.reduce((s, d) => s + d.count, 0) || 1;
                      const palette = ['bg-coffee-900', 'bg-coffee-400', 'bg-coffee-200', 'bg-coffee-100'];
                      return (
                        <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-6 sm:p-8">
                          <p className="eyebrow mb-1.5">Visitor mix</p>
                          <h2 className="font-display text-xl sm:text-2xl leading-tight text-coffee-900 dark:text-cream-50 tracking-tight mb-6">
                            Devices<span className="text-accent-500">.</span>
                          </h2>
                          <div className="h-7 flex rounded overflow-hidden border border-latte-300 dark:border-latte-800" role="img" aria-label="Device mix">
                            {analytics.devices.map((d, i) => (
                              <div
                                key={i}
                                className={`${palette[i % palette.length]} transition-[width] duration-700 ease-out motion-reduce:transition-none`}
                                style={{ width: `${(d.count / total) * 100}%` }}
                                title={`${d.label}: ${d.count}`}
                              />
                            ))}
                          </div>
                          <ul className="mt-5 space-y-3 text-sm">
                            {analytics.devices.map((d, i) => (
                              <li key={i} className="flex items-center justify-between">
                                <span className="flex items-center gap-2 capitalize">
                                  <span className={`w-2.5 h-2.5 rounded-sm ${palette[i % palette.length]}`} />
                                  <span className="text-coffee-900 dark:text-cream-50">{d.label}</span>
                                </span>
                                <span className="font-mono text-coffee-700 dark:text-cream-200">
                                  {d.count.toLocaleString()} · {((d.count / total) * 100).toFixed(0)}%
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}

                    <div className="grid grid-cols-2 gap-4">
                      {analytics.operatingSystems.length > 0 && (
                        <BreakdownCard label="Top OS" rows={analytics.operatingSystems} />
                      )}
                      {analytics.browsers.length > 0 && (
                        <BreakdownCard label="Top browsers" rows={analytics.browsers} />
                      )}
                    </div>
                  </div>
                </section>

                {/* Locations */}
                {(analytics.countries.length > 0 || analytics.cities.length > 0) && (
                  <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {analytics.countries.length > 0 && (
                      <RankedList
                        eyebrow="Geography"
                        title="Top countries"
                        countLabel={`${analytics.countries.length} total`}
                        rows={analytics.countries}
                        showPercent
                      />
                    )}
                    {analytics.cities.length > 0 && (
                      <RankedList
                        eyebrow="Geography"
                        title="Top cities"
                        countLabel={`${analytics.cities.length} total`}
                        rows={analytics.cities}
                      />
                    )}
                  </section>
                )}
              </>
            )}

            {!isPaidUser && (
              <ProUpsell
                title="Full audience data"
                body="Pro users get geographic breakdowns, device mix, and traffic sources."
              />
            )}

            <Footnotes />
          </div>
        )}

        {/* ── Links tab ── */}
        {statsSubTab === 'links' && (
          <div className="space-y-8">
            {links.length === 0 ? (
              <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-12 text-center">
                <div className="w-12 h-12 rounded bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 flex items-center justify-center mx-auto mb-5">
                  <LinkIcon className="w-5 h-5 text-coffee-400 dark:text-cream-400" />
                </div>
                <div className="eyebrow mb-3">No links yet</div>
                <h3 className="font-display text-2xl text-coffee-900 dark:text-cream-50 mb-2">No links on this card.</h3>
                <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-xs mx-auto mb-5">Add links to your card to start tracking clicks.</p>
                <Button to="/my-card" variant="primary">Add links</Button>
              </div>
            ) : (
              <>
                <section className="bg-cream-100 dark:bg-coffee-800 rounded-lg overflow-hidden">
                  <header className="flex items-end justify-between gap-3 px-6 sm:px-8 pt-7 pb-5 border-b border-latte-300 dark:border-latte-800">
                    <div>
                      <p className="eyebrow mb-1.5">Performance</p>
                      <h2 className="font-display text-xl sm:text-2xl leading-tight text-coffee-900 dark:text-cream-50 tracking-tight">
                        Links on your card<span className="text-accent-500">.</span>
                      </h2>
                    </div>
                    <Button to="/my-card" variant="ghost" size="sm">Manage links →</Button>
                  </header>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-latte-300 dark:border-latte-800">
                          <th className="text-left px-6 sm:px-8 py-3 eyebrow">Link</th>
                          <th className="text-left px-4 py-3 eyebrow hidden md:table-cell">Destination</th>
                          <th className="text-right px-4 py-3 eyebrow">Clicks</th>
                          <th className="text-right px-4 py-3 eyebrow hidden sm:table-cell">CTR</th>
                          {isPaidUser && <th className="text-right px-6 sm:px-8 py-3 eyebrow">Trend</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-latte-300">
                        {links.map((link, idx) => {
                          const clicks = linkClicks[link.id] || 0;
                          const maxClicks = Math.max(...Object.values(linkClicks), 1);
                          const pct = (clicks / maxClicks) * 100;
                          const ctr = visitStats && visitStats.totalVisits > 0
                            ? ((clicks / visitStats.totalVisits) * 100).toFixed(1)
                            : '0.0';
                          const sparkline = perLinkSparklines[link.id] || [];
                          const isTop = idx === 0 && clicks > 0;
                          return (
                            <tr key={link.id}>
                              <td className="px-6 sm:px-8 py-4">
                                <div className="flex items-center gap-3">
                                  <span className="w-7 h-7 rounded bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 grid place-items-center flex-shrink-0">
                                    {link.customIcon
                                      ? <img src={link.customIcon} alt="" className="w-3.5 h-3.5 rounded object-cover" />
                                      : <SocialIcon name={link.icon || 'link'} className="w-3.5 h-3.5 text-coffee-600 dark:text-cream-200" />
                                    }
                                  </span>
                                  <span className="font-medium text-coffee-900 dark:text-cream-50 truncate max-w-[180px]">{link.title}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 hidden md:table-cell">
                                <span className="font-mono text-xs text-coffee-500 dark:text-cream-300 truncate block max-w-[260px]">{link.url}</span>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex items-center justify-end gap-3">
                                  <div className="w-24 bg-cream-50 dark:bg-coffee-900 border border-latte-300 dark:border-latte-800 rounded-full h-1.5 hidden md:block" aria-hidden="true">
                                    <div
                                      className={`h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none ${isTop ? 'bg-accent-500' : 'bg-coffee-900'}`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                  <span className="font-mono text-coffee-900 dark:text-cream-50 tabular-nums">{clicks.toLocaleString()}</span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-right hidden sm:table-cell">
                                <span className="font-mono text-coffee-700 dark:text-cream-200">{ctr}%</span>
                              </td>
                              {isPaidUser && (
                                <td className="px-6 sm:px-8 py-4 text-right">
                                  {sparkline.length > 1 ? <MicroSparkline points={sparkline} /> : <span className="text-coffee-400 dark:text-cream-400 text-xs">—</span>}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>

                {!isPaidUser && (
                  <ProUpsell
                    title="Per-link trend lines"
                    body="Pro shows daily click trends for every link on your card."
                  />
                )}
              </>
            )}

            <Footnotes />
          </div>
        )}

      </main>
    </div>
  );
};

// ───────── Sub-components (private) ─────────

const HeroSparkline: React.FC<{ points: number[] }> = ({ points }) => {
  const w = 320, h = 120;
  const { line, area, coords } = svgPath(points, w, h, 8);
  const last = coords?.[coords.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img" aria-label="Daily card views over the last 7 days">
      <defs>
        <pattern id="ins-grid" width="40" height="30" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#dcc6a3" strokeWidth="0.5" strokeDasharray="2 3" />
        </pattern>
      </defs>
      <rect width={w} height={h} fill="url(#ins-grid)" opacity="0.6" />
      <path d={area} fill="rgba(184, 82, 26, 0.08)" />
      <path d={line} fill="none" stroke="#b8521a" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {coords?.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === coords.length - 1 ? 4 : 3} fill={i === coords.length - 1 ? '#fbf7f1' : '#b8521a'} stroke={i === coords.length - 1 ? '#b8521a' : 'none'} strokeWidth={i === coords.length - 1 ? 2 : 0} />
      ))}
      {last && (
        <line x1={last[0]} y1={last[1]} x2={last[0]} y2={h - 4} stroke="#b8521a" strokeWidth="0.75" strokeDasharray="2 3" />
      )}
    </svg>
  );
};

const BreakdownCard: React.FC<{ label: string; rows: { label: string; count: number }[] }> = ({ label, rows }) => {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-5">
      <p className="eyebrow mb-3">{label}</p>
      <ul className="space-y-2 text-sm">
        {rows.slice(0, 4).map((r, i) => (
          <li key={i} className="flex justify-between">
            <span className="text-coffee-900 dark:text-cream-50 truncate pr-2">{r.label}</span>
            <span className="font-mono text-coffee-700 dark:text-cream-200 flex-shrink-0">{Math.round((r.count / total) * 100)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const RankedList: React.FC<{
  eyebrow: string;
  title: string;
  countLabel: string;
  rows: { label: string; count: number }[];
  showPercent?: boolean;
}> = ({ eyebrow, title, countLabel, rows, showPercent }) => {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-6 sm:p-8">
      <header className="flex items-end justify-between gap-3 mb-6">
        <div>
          <p className="eyebrow mb-1.5">{eyebrow}</p>
          <h2 className="font-display text-xl sm:text-2xl leading-tight text-coffee-900 dark:text-cream-50 tracking-tight">
            {title}<span className="text-accent-500">.</span>
          </h2>
        </div>
        <span className="font-mono text-xs text-coffee-500 dark:text-cream-300">{countLabel}</span>
      </header>
      <ol className="text-sm divide-y divide-latte-300">
        {rows.slice(0, 6).map((r, i) => (
          <li key={i} className="flex items-center justify-between py-2.5">
            <span className="flex items-center gap-3 min-w-0">
              <span className="font-mono text-xs text-coffee-400 dark:text-cream-400 w-5 flex-shrink-0">{String(i + 1).padStart(2, '0')}</span>
              <span className="text-coffee-900 dark:text-cream-50 truncate">{r.label}</span>
            </span>
            <span className="font-mono text-coffee-700 dark:text-cream-200 flex-shrink-0 ml-3">
              {r.count.toLocaleString()}
              {showPercent && <span className="text-coffee-400 dark:text-cream-400"> · {((r.count / total) * 100).toFixed(1)}%</span>}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const ProUpsell: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <section className="bg-cream-100 dark:bg-coffee-800 rounded-lg p-8 sm:p-12 text-center">
    <p className="eyebrow mb-3">Pro feature</p>
    <h3 className="font-display text-2xl sm:text-3xl text-coffee-900 dark:text-cream-50 mb-2 tracking-tight">
      {title}<span className="text-accent-500">.</span>
    </h3>
    <p className="text-sm text-coffee-600 dark:text-cream-200 max-w-md mx-auto mb-5">{body}</p>
    <Button to="/pricing" variant="primary" size="sm">View Pro plan</Button>
  </section>
);

const Footnotes: React.FC = () => (
  <section className="border-t border-latte-300 dark:border-latte-800 pt-6">
    <ol className="font-mono text-[11px] text-coffee-500 dark:text-cream-300 space-y-1.5">
      <li><sup>1</sup> Visit counts include all loads of your public card page; analytics-blocked browsers may still show a partial view.</li>
      <li><sup>2</sup> Trend baselines compare equivalent rolling windows (7-over-7). Empty baselines suppress the comparison.</li>
    </ol>
  </section>
);

export default Insights;
